"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Button, Spinner, Row, Col } from "reactstrap"
import {
  FaCalendarAlt,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaDollarSign,
  FaTicketAlt,
  FaUsers,
  FaPrint,
  FaCheckCircle,
  FaMoneyBillWave,
} from "react-icons/fa"
import analyticsService from "../../../Service/dashboardService"
import type { AnalyticsResponse } from "./types/dashboard"
import { formatCurrency } from "./dashboardHelpers"
import "../../CommonElements/DashboardTheme/dashboard-theme.css"

interface AnalyticsDashboardProps {
  className?: string
}

// Known operator branding. Place the actual PNG logo files in the public
// folder at these paths — e.g. public/assets/images/operators/mtn.png and
// public/assets/images/operators/airtel.png — referenced here via
// PUBLIC_URL the same way the rest of the app links to public assets
// (see CommonLogo.tsx / LoginForm.tsx). Until a file exists at that path,
// or for any operator not in this map, a brand-colored chip is shown
// instead so the tables always look finished.
const PUBLIC_URL = process.env.PUBLIC_URL || ""

const OPERATOR_BRANDS: Record<string, { logo: string; bg: string; color: string; initials: string }> = {
  mtn: { logo: `${PUBLIC_URL}/assets/images/operators/mtn.png`, bg: "#FFCC00", color: "#1a1a1a", initials: "MTN" },
  airtel: {
    logo: `${PUBLIC_URL}/assets/images/operators/airtel.png`,
    bg: "#ED1C24",
    color: "#ffffff",
    initials: "Airtel",
  },
}

const OperatorBadge: React.FC<{ operator: string }> = ({ operator }) => {
  const [imgFailed, setImgFailed] = useState(false)
  const brand = OPERATOR_BRANDS[operator.trim().toLowerCase()]

  if (brand && !imgFailed) {
    return (
      <div className="db-operator-badge">
        <img
          src={brand.logo}
          alt={`${operator} logo`}
          className="db-operator-logo"
          onError={() => setImgFailed(true)}
        />
        <span className="db-operator-name">{operator}</span>
      </div>
    )
  }

  return (
    <span
      className="db-operator-chip"
      style={{ background: brand?.bg || "var(--db-slate-soft)", color: brand?.color || "var(--db-slate)" }}
    >
      {brand?.initials || operator}
    </span>

  )
}

export default function AnalyticsDashboard({ className }: AnalyticsDashboardProps) {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAnalytics = async () => {
    setLoading(true)
    setError(null)

    try {
      // Always fetch yesterday and today
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      const params = {
        start_date: yesterday.toISOString().split("T")[0],
        end_date: today.toISOString().split("T")[0],
      }

      const data = await analyticsService.getAnalyticsComparison(params)
      setAnalyticsData(data)
    } catch (error) {
      console.error("Error fetching analytics:", error)
      setError("Failed to load analytics data. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAnalytics()
  }, [])

  const getTrendIcon = (change: number) => {
    if (change > 0) return <FaArrowUp className="me-1" />
    if (change < 0) return <FaArrowDown className="me-1" />
    return <FaMinus className="me-1" />
  }

  const getTrendClass = (change: number) => {
    if (change > 0) return "db-trend-pill db-trend-up"
    if (change < 0) return "db-trend-pill db-trend-down"
    return "db-trend-pill db-trend-flat"
  }

  const SectionHeader = () => (
    <div className="db-section-head">
      <span className="db-section-bar db-bg-brand"></span>
      <div className="flex-grow-1">
        <p className="db-section-eyebrow">Yesterday vs Today</p>
        <h3 className="db-section-title">Analytics Comparison</h3>
      </div>
      <Button size="sm" className="db-btn-brand" onClick={fetchAnalytics} disabled={loading}>
        {loading ? <Spinner size="sm" className="me-1" /> : <FaCalendarAlt className="me-1" />}
        Refresh
      </Button>
    </div>
  )

  if (error) {
    return (
      <div className={className}>
        <SectionHeader />
        <div className="db-card text-center p-4">
          <h5 className="db-text-rose">Error Loading Analytics</h5>
          <p className="text-muted mb-3">{error}</p>
          <Button className="db-btn-brand" onClick={fetchAnalytics}>
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className={className}>
        <SectionHeader />
        <div className="db-card text-center py-4">
          <Spinner className="db-text-brand" />
          <p className="mt-3 mb-0 text-muted">Loading analytics comparison…</p>
        </div>
      </div>
    )
  }

  if (!analyticsData || analyticsData.data.length < 2) {
    return (
      <div className={className}>
        <SectionHeader />
        <div className="db-card text-center p-4">
          <p className="text-muted mb-3">Insufficient data for comparison. Need at least 2 days of data.</p>
          <Button className="db-btn-brand" onClick={fetchAnalytics}>
            Refresh
          </Button>
        </div>
      </div>
    )
  }

  const yesterdayData = analyticsData.data[0]
  const todayData = analyticsData.data[1]

  const CompareCard = ({
    icon,
    metricLabel,
    yesterdayValue,
    todayValue,
    change,
    footnote,
  }: {
    icon: React.ReactNode
    metricLabel: string
    yesterdayValue: React.ReactNode
    todayValue: React.ReactNode
    change?: number
    footnote?: React.ReactNode
  }) => (
    <Col lg="4" md="6" className="mb-3">
      <div className="db-compare-card">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <div className="d-flex align-items-center justify-content-center gap-3">
              <div className="text-center">
                <div className="db-compare-label">Yesterday</div>
                <div className="db-compare-value">{yesterdayValue}</div>
              </div>
              <div className="db-compare-divider">→</div>
              <div className="text-center">
                <div className="db-compare-label">Today</div>
                <div className="db-compare-value db-text-brand">{todayValue}</div>
              </div>
            </div>
            <div className="db-compare-metric-name">{metricLabel}</div>
            {change !== undefined && (
              <div className="d-flex justify-content-center mt-2">
                <span className={getTrendClass(change)}>
                  {getTrendIcon(change)}
                  {Math.abs(change)}%
                </span>
              </div>
            )}
            {footnote && <div className="text-center mt-2">{footnote}</div>}
          </div>
          <div
            className="db-kpi-chip db-bg-brand-soft db-text-brand"
            style={{ width: 34, height: 34 }}
          >
            {icon}
          </div>
        </div>
      </div>
    </Col>
  )

  return (
    <div className={className}>
      <SectionHeader />

      {/* Summary Cards */}
      <Row className="mb-4">
        <CompareCard
          icon={<FaDollarSign size={15} />}
          metricLabel="Total Revenue"
          yesterdayValue={formatCurrency(yesterdayData.totals.totalRevenue)}
          todayValue={formatCurrency(todayData.totals.totalRevenue)}
          change={todayData.comparisons?.totalRevenueChange}
        />

        <CompareCard
          icon={<FaMoneyBillWave size={15} />}
          metricLabel="Paid Visits"
          yesterdayValue={yesterdayData.totals.totalPaidVisits}
          todayValue={todayData.totals.totalPaidVisits}
          change={todayData.comparisons?.paidVisitsChange}
        />

        <CompareCard
          icon={<FaUsers size={15} />}
          metricLabel="Whitelisted"
          yesterdayValue={yesterdayData.totals.totalWhitelisted}
          todayValue={todayData.totals.totalWhitelisted}
          change={todayData.comparisons?.whitelistedChange}
        />

        <CompareCard
          icon={<FaTicketAlt size={15} />}
          metricLabel="Tickets Sold"
          yesterdayValue={yesterdayData.totals.totalTickets}
          todayValue={todayData.totals.totalTickets}
          footnote={
            <small className="text-muted fw-bold">
              Rev: {formatCurrency(yesterdayData.totals.ticketRevenue)} → {formatCurrency(todayData.totals.ticketRevenue)}
            </small>
          }
        />

        <CompareCard
          icon={<FaCheckCircle size={15} />}
          metricLabel="Tickets Used"
          yesterdayValue={yesterdayData.totals.totalUsedTickets}
          todayValue={todayData.totals.totalUsedTickets}
          change={todayData.comparisons?.usedTicketsChange}
        />

        <CompareCard
          icon={<FaPrint size={15} />}
          metricLabel="Badge Prints"
          yesterdayValue={yesterdayData.badgeStats.prints}
          todayValue={todayData.badgeStats.prints}
          footnote={
            <small className="text-muted fw-bold">
              Rev: {formatCurrency(yesterdayData.badgeStats.revenue)} → {formatCurrency(todayData.badgeStats.revenue)}
            </small>
          }
        />
      </Row>

      {/* Operator Comparison Tables */}
      <Row className="mb-3">
        <Col lg="6" className="mb-3">
          <div
            className="db-chart-card p-0"
            style={{ overflow: "hidden", borderTop: "3px solid var(--db-emerald)" }}
          >
            <div className="px-3 pt-3 pb-2">
              <h6 className="mb-0 fw-bold db-text-emerald">
                <i className="fa fa-truck me-2"></i>
                Vehicle Revenue by Operator (Paid Only)
              </h6>
            </div>
            <div className="table-responsive px-2 pb-2">
              <table className="table table-sm db-table mb-0">
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: "25%" }}>
                      Metric
                    </th>
                    {analyticsData.operators.map((operator) => (
                      <th
                        key={operator}
                        className="text-center"
                        style={{ width: `${75 / analyticsData.operators.length}%` }}
                      >
                        <OperatorBadge operator={operator} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="fw-bold">Yesterday Visits</td>
                    {analyticsData.operators.map((operator) => (
                      <td key={operator} className="text-center">
                        {yesterdayData.vehicleOperators[operator]?.visits || 0}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="fw-bold">Today Visits</td>
                    {analyticsData.operators.map((operator) => (
                      <td key={operator} className="text-center fw-bold db-text-brand">
                        {todayData.vehicleOperators[operator]?.visits || 0}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="fw-bold">Yesterday Revenue</td>
                    {analyticsData.operators.map((operator) => (
                      <td key={operator} className="text-center">
                        {formatCurrency(yesterdayData.vehicleOperators[operator]?.revenue || 0)}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="fw-bold">Today Revenue</td>
                    {analyticsData.operators.map((operator) => (
                      <td key={operator} className="text-center fw-bold db-text-emerald">
                        {formatCurrency(todayData.vehicleOperators[operator]?.revenue || 0)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </Col>

        <Col lg="6" className="mb-3">
          <div
            className="db-chart-card p-0"
            style={{ overflow: "hidden", borderTop: "3px solid var(--db-cyan)" }}
          >
            <div className="px-3 pt-3 pb-2">
              <h6 className="mb-0 fw-bold db-text-cyan">
                <i className="fa fa-ticket me-2"></i>
                Ticket Sales by Operator
              </h6>
            </div>
            <div className="table-responsive px-2 pb-2">
              <table className="table table-sm db-table mb-0">
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: "25%" }}>
                      Metric
                    </th>
                    {analyticsData.operators.map((operator) => (
                      <th
                        key={operator}
                        className="text-center"
                        style={{ width: `${75 / analyticsData.operators.length}%` }}
                      >
                        <OperatorBadge operator={operator} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="fw-bold">Yesterday Tickets</td>
                    {analyticsData.operators.map((operator) => (
                      <td key={operator} className="text-center">
                        {yesterdayData.ticketOperators[operator]?.count || 0}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="fw-bold">Today Tickets</td>
                    {analyticsData.operators.map((operator) => (
                      <td key={operator} className="text-center fw-bold db-text-cyan">
                        {todayData.ticketOperators[operator]?.count || 0}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="fw-bold">Yesterday Revenue</td>
                    {analyticsData.operators.map((operator) => (
                      <td key={operator} className="text-center">
                        {formatCurrency(yesterdayData.ticketOperators[operator]?.revenue || 0)}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="fw-bold">Today Revenue</td>
                    {analyticsData.operators.map((operator) => (
                      <td key={operator} className="text-center fw-bold db-text-emerald">
                        {formatCurrency(todayData.ticketOperators[operator]?.revenue || 0)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  )
}
